import type { ScriptStatus } from '../../../shared/types';
import { currentRound, feedbackThisRound, reminderState } from '../../../shared/publish';
import { dayWhen, longWhen, relativeDay, shortWhen, type PublishInfo } from '../../lib/publish';

export interface RemindItem {
  enabled: boolean;
  /** The menu's small line: a gloss when on, the reason when off. */
  line: string;
  round: number;
  /** Confirm dialog sentence. */
  confirmBody: string;
}

/** Copy for the Remind client menu item (mock-up 05 §04, "When Remind client shows"). */
export function remindItem(args: {
  status: ScriptStatus;
  archivedAt?: number | null;
  clientEmail: string | null;
  info: PublishInfo;
  now?: number;
}): RemindItem {
  const { status, clientEmail, info } = args;
  const now = args.now ?? Date.now();
  const round = currentRound(status, info.published_round) ?? 1;
  const feedback = feedbackThisRound(info.last_client_feedback_at, info.round_published_at);
  const state = reminderState({
    status,
    archivedAt: args.archivedAt,
    clientEmail,
    emailEnabled: info.email_enabled,
    lastRemindedAt: info.last_reminded_at,
    feedbackThisRound: feedback,
    now,
  });
  const last = info.last_reminded_at;
  const confirmBody = `Sends “v${round} is waiting for your review” to ${clientEmail ?? 'the client'}.${
    last ? ` Last reminded ${longWhen(last)}.` : ''
  }`;
  const off = (line: string): RemindItem => ({ enabled: false, line, round, confirmBody });
  switch (state.reason) {
    case 'not_waiting':
      return off('Nothing waiting for review');
    case 'no_email':
      return off('No client email on this script');
    case 'email_off':
      return off('Client emails are off');
    case 'feedback_in':
      return off(`${info.last_client_feedback_by || 'The client'} sent feedback on ${dayWhen(info.last_client_feedback_at!)}`);
    case 'cooldown':
      return off(`Reminded ${relativeDay(last!, now)} · again from ${relativeDay(state.nextAt!, now)}`);
    default:
      return {
        enabled: true,
        line: last ? `Last reminded ${shortWhen(last, now)}` : `Sends “v${round} is waiting for your review”`,
        round,
        confirmBody,
      };
  }
}
